import { Table, TableBody, TableCell, TableRow } from '@material-ui/core';
import i18n from '@opsbase/lib-i18n';
import { Palette } from '@opsbase/lib-palette';
import useSelf from '@opsbase/react-ctrl-self/useSelf';
import useSelfActions from '@opsbase/react-ctrl-self/useSelfActions/useSelfActions';
import useOrgTestIoT from '@opsbase/react-gql-hooks/organisation/useOrgTestIoT';
import useOrgUpdate from '@opsbase/react-gql-hooks/organisation/useOrgUpdate';
import useCallforward from '@opsbase/react-hooks/useCallforward/useCallforward';
import markdown from '@opsbase/react-markdown';
import AppPage from '@opsbase/web-app/AppPage';
import useCss from '@opsbase/web-css';
import Card from '@opsbase/web-layout/Card';
import Divider from '@opsbase/web-layout/Divider';
import Horizontal from '@opsbase/web-layout/Horizontal';
import Pane from '@opsbase/web-layout/Pane';
import Vertical from '@opsbase/web-layout/Vertical';
import TextFieldWithPasswordShowHide from '@opsbase/web-textfield/TextFieldWithPasswordShowHide';
import TextFieldWithStateAndBlur from '@opsbase/web-textfield/TextFieldWithStateAndBlur';
import Button from '@opsbase/web-ui/Button';
import Text from '@opsbase/web-ui/Text';
import React, { useCallback, useState } from 'react';
const makeMyClasses = () => ({
    textBox: {
        minWidth: 300,
    },
});
// eslint-disable-next-line no-shadow
var TESTING;
(function (TESTING) {
    TESTING[TESTING["NONE"] = 0] = "NONE";
    TESTING[TESTING["PROCESSING"] = 1] = "PROCESSING";
    TESTING[TESTING["SUCCEEDED"] = 2] = "SUCCEEDED";
    TESTING[TESTING["FAILED"] = 3] = "FAILED";
})(TESTING || (TESTING = {}));
export function AdvancedIntegrationsIoTPage() {
    var _a, _b, _c;
    const self = useSelf();
    const { updateSettings } = useSelfActions();
    const updateOrg = useOrgUpdate();
    const [testing, setTesting] = useState(TESTING.NONE);
    const [dTech, setDTech] = useState((_b = (_a = self === null || self === void 0 ? void 0 : self.settings) === null || _a === void 0 ? void 0 : _a.integrations) === null || _b === void 0 ? void 0 : _b.disruptiveTech);
    const [deviceInfo, setDeviceInfo] = useState();
    const testIoT = useOrgTestIoT();
    const myCss = useCss(makeMyClasses);
    const { serviceAccountEmail, serviceAccountKeyId, serviceAccountSecret } = dTech || {};
    const testEnabled = serviceAccountSecret && serviceAccountKeyId && serviceAccountEmail;
    const handleChange = useCallforward(field => value => {
        const disruptiveTech = {
            ...self.settings.integrations.disruptiveTech,
            [field]: value,
        };
        setDTech(disruptiveTech);
        return updateOrg({
            id: self.organisationId,
            patch: { settings: { integrations: { disruptiveTech } } },
        }).then(updatedOrg => updateSettings(updatedOrg.settings));
    }, [self, updateOrg, updateSettings]);
    const handleTestPressed = useCallback(async () => {
        setTesting(TESTING.PROCESSING);
        try {
            const v = await testIoT();
            if (v) {
                setDeviceInfo(v);
                setTesting(TESTING.SUCCEEDED);
            }
            else {
                setTesting(TESTING.FAILED);
            }
        }
        catch (_a) {
            setTesting(TESTING.FAILED);
        }
    }, [testIoT]);
    return (React.createElement(AppPage, null,
        React.createElement(Vertical, { gutter: true, padding: true, style: { overflow: 'auto' } },
            React.createElement(Card, { padding: true, style: { maxWidth: 500 } },
                React.createElement(Horizontal, { centered: true },
                    React.createElement(Text, { variant: "h3" }, markdown(i18n.t('dtech-service-account-email'))),
                    React.createElement(TextFieldWithStateAndBlur, { className: myCss.textBox, value: serviceAccountEmail, onBlur: handleChange('serviceAccountEmail') })),
                React.createElement(Horizontal, { centered: true },
                    React.createElement(Text, { variant: "h3" }, markdown(i18n.t('dtech-service-account-key-id'))),
                    React.createElement(TextFieldWithStateAndBlur, { className: myCss.textBox, value: serviceAccountKeyId, onBlur: handleChange('serviceAccountKeyId') })),
                React.createElement(Horizontal, { centered: true },
                    React.createElement(Text, { variant: "h3" }, markdown(i18n.t('dtech-service-account-secret'))),
                    React.createElement(TextFieldWithPasswordShowHide, { className: myCss.textBox, value: serviceAccountSecret, onBlur: handleChange('serviceAccountSecret') })),
                React.createElement(Pane, { right: true, padding: true },
                    React.createElement(Button, { label: i18n.t('test'), onClick: handleTestPressed, style: { marginRight: 8 }, disabled: !testEnabled, loading: testing === TESTING.PROCESSING })),
                (testing === TESTING.SUCCEEDED || testing === TESTING.FAILED) && (React.createElement(Vertical, { style: {
                        margin: 16,
                        padding: 16,
                    } },
                    testing === TESTING.SUCCEEDED && (React.createElement(React.Fragment, null,
                        React.createElement(Text, { variant: "h2" }, i18n.t('dtech-found-devices')),
                        React.createElement(Table, null,
                            React.createElement(TableBody, null, (_c = deviceInfo === null || deviceInfo === void 0 ? void 0 : deviceInfo.keys) === null || _c === void 0 ? void 0 : _c.map(key => {
                                const devices = deviceInfo[key];
                                return (React.createElement(TableRow, { style: {
                                        backgroundColor: Palette.CardKeyline.main,
                                    }, key: key },
                                    React.createElement(TableCell, null,
                                        React.createElement(Text, { variant: "h1" }, deviceInfoLabel(key))),
                                    React.createElement(TableCell, null,
                                        React.createElement(Text, { variant: "h1" }, devices.length))));
                            }))))),
                    testing === TESTING.FAILED && (React.createElement(Text, { variant: "h2" }, i18n.t('dtech-testing-failed'))))),
                React.createElement(Divider, null),
                React.createElement(Text, { variant: "body" }, markdown(i18n.t('dtech-service-account-intro')))))));
}
export default AdvancedIntegrationsIoTPage;
function deviceInfoLabel(key) {
    switch (key) {
        case 'humidity':
            return i18n.t('dt-humidity');
        case 'proximity':
            return i18n.t('dt-proximity');
        case 'proximityCounter':
            return i18n.t('dt-proximitycounter');
        case 'temperature':
            return i18n.t('dt-temperature');
        case 'touch':
            return i18n.t('dt-touch');
        case 'touchCounter':
            return i18n.t('dt-touchcounter');
        case 'waterDetector':
            return i18n.t('dt-waterdetector');
    }
}
